import { api } from '../../../utility/apiClient';
import { BASE_URL } from '../../../config/clientConfig';

/**
 * GET /api/v1/users
 * Params: page, limit, role, plan_tier, email_verified, is_active
 */
export const adminService = {
  async getUsers({ page = 1, limit = 10, planTier, role, emailVerified, isActive, search } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (planTier      && planTier !== 'all')  params.set('plan_tier',       planTier);
    if (role          && role    !== 'all')   params.set('role',            role);
    if (emailVerified !== undefined)          params.set('email_verified',  String(emailVerified));
    if (isActive      !== undefined)          params.set('is_active',       String(isActive));
    if (search?.trim())                       params.set('search',          search.trim());

    return api.get(`/api/v1/users?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch users.'); });
  },

  /**
   * PATCH /api/v1/users/:id
   * Payload: { name, plan_tier, is_active }
   */
  async updateUser(id, data) {
    return api.put(`/api/v1/users/${id}`, data)
      .catch((err) => { throw new Error(err.message || 'Failed to update user.'); });
  },

  /**
   * GET /api/v1/admin/overview
   * Returns platform-wide metrics and user growth data.
   */
  async getOverview() {
    return api.get('/api/v1/admin/overview')
      .catch((err) => { throw new Error(err.message || 'Failed to fetch overview.'); });
  },

  async getUserSignupOverviewReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/user-signup-overview?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch user signup overview report.'); });
  },

  /** GET /api/v1/admin/reports/plan-mix-revenue?page=1&limit=25 */
  async getPlanMixReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/plan-mix-revenue?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch plan mix report.'); });
  },

  /** GET /api/v1/admin/reports/plan-mix-revenue/export */
  async downloadPlanMixExport({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/plan-mix-revenue/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch { /* ignore */ }
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawName = fileMatch?.[1] || fileMatch?.[2] || 'plan-mix-billing-signal.xlsx';
    const filename = decodeURIComponent(rawName);
    return { blob, filename };
  },

  /** GET /api/v1/admin/reports/asset-inventory?page=1&limit=25 */
  async getAssetInventoryReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/asset-inventory?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch asset inventory report.'); });
  },

  /** GET /api/v1/admin/reports/asset-inventory/export */
  async downloadAssetInventoryExport({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/asset-inventory/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch {
        // ignore non-json error body
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawName = fileMatch?.[1] || fileMatch?.[2] || 'asset-inventory.csv';
    const filename = decodeURIComponent(rawName);
    return { blob, filename };
  },

  /** GET /api/v1/admin/reports/report-generation-volume */
  async getReportGenerationVolumeReport({ startDate, endDate } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    return api.get(`/api/v1/admin/reports/report-generation-volume${qs ? '?' + qs : ''}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch report generation volume report.'); });
  },

  /** GET /api/v1/admin/reports/stale-analyses */
  async getStaleOrStuckReport({ startDate, endDate } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    return api.get(`/api/v1/admin/reports/stale-analyses${qs ? '?' + qs : ''}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch stale or stuck work report.'); });
  },

  /** GET /api/v1/admin/reports/enterprise-funnel?page=1&limit=25 */
  async getEnterpriseFunnelReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/enterprise-funnel?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch enterprise funnel report.'); });
  },

  /** GET /api/v1/admin/reports/enterprise-funnel/export */
  async downloadEnterpriseFunnelExport({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/enterprise-funnel/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch { /* ignore */ }
      throw new Error(message);
    }
    return response.blob();
  },

  /** GET /api/v1/admin/reports/activity-audit?page=1&limit=25 */
  async getActivityAuditReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/activity-audit?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch activity audit report.'); });
  },

  /** GET /api/v1/admin/reports/plan-vs-usage?page=1&limit=25 */
  async getPlanVsUsageReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/plan-vs-usage?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch plan vs usage report.'); });
  },

  /** GET /api/v1/admin/reports/analysis-pipeline?page=1&limit=25 */
  async getAnalysisPipelineReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/analysis-pipeline?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch analysis pipeline report.'); });
  },

  /** GET /api/v1/admin/reports/analysis-pipeline/export */
  async downloadAnalysisPipelineExport({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/analysis-pipeline/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch {
        // ignore non-json error body
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawName = fileMatch?.[1] || fileMatch?.[2] || 'analysis-pipeline-health.csv';
    const filename = decodeURIComponent(rawName);
    return { blob, filename };
  },

  /** GET /api/v1/admin/reports/power-users?page=1&limit=25 */
  async getPowerUsersReport({ page = 1, limit = 25, startDate, endDate } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return api.get(`/api/v1/admin/reports/power-users?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch power users report.'); });
  },

  /** GET /api/v1/admin/reports/power-users/export */
  async downloadPowerUsersExport({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/power-users/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      let message = `Export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch {
        // ignore non-json error body
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawName = fileMatch?.[1] || fileMatch?.[2] || 'power-users.csv';
    const filename = decodeURIComponent(rawName);
    return { blob, filename };
  },

  async downloadUserSignupOverviewCsv({ startDate, endDate } = {}) {
    const token = localStorage.getItem('auth_token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate) qs.set('endDate', endDate);
    const qsStr = qs.toString();
    const response = await fetch(`${BASE_URL}/api/v1/admin/reports/user-signup-overview/export${qsStr ? '?' + qsStr : ''}`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      let message = `CSV export failed: ${response.status}`;
      try {
        const errBody = await response.json();
        message = errBody?.message || errBody?.error || message;
      } catch {
        // ignore non-json error body
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition') || '';
    const fileMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const rawName = fileMatch?.[1] || fileMatch?.[2] || 'user-signup-overview.csv';
    const filename = decodeURIComponent(rawName);

    return { blob, filename };
  },

  // ── Plans ──────────────────────────────────────────────────────────────────

  /** GET /api/v1/users/:id */
  async getUser(id) {
    return api.get(`/api/v1/users/${id}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch user.'); });
  },

  /** GET /api/v1/history/user/:userId */
  async getUserAnalysisHistory(userId) {
    return api.get(`/api/v1/history/user/${userId}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch analysis history.'); });
  },

  /** GET /api/v1/history/asset/:assetId/report */
  async getAssetReport(assetId) {
    return api.get(`/api/v1/history/asset/${assetId}/report`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch report.'); });
  },

  /** GET /api/v1/payments/stripe-history/:userId */
  async getPaymentHistory(userId, { limit = 10, cursor } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('starting_after', cursor);
    return api.get(`/api/v1/payments/stripe-history/${userId}?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch payment history.'); });
  },

  /** GET /api/v1/payments/admin-history */
  async getAdminPaymentHistory({ limit = 10, cursor, search, planTier, status } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor)                              params.set('starting_after', cursor);
    if (search?.trim())                      params.set('search',         search.trim());
    if (planTier && planTier !== 'all')      params.set('plan_tier',      planTier);
    if (status   && status   !== 'all')      params.set('status',         status);
    return api.get(`/api/v1/payments/admin-history?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch admin payment history.'); });
  },

  /** GET /api/v1/plans */
  async getPlans() {
    return api.get('/api/v1/plans')
      .catch((err) => { throw new Error(err.message || 'Failed to fetch plans.'); });
  },

  /** GET /api/v1/plans/:id */
  async getPlan(id) {
    return api.get(`/api/v1/plans/${id}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch plan.'); });
  },

  /**
   * POST /api/v1/plans
   * Payload: { name, price, analyses_per_month, is_popular, features, is_active }
   */
  async createPlan(data) {
    return api.post('/api/v1/plans', data)
      .catch((err) => { throw new Error(err.message || 'Failed to create plan.'); });
  },

  /**
   * PATCH /api/v1/plans/:id
   * Payload: partial plan fields
   */
  async updatePlan(id, data) {
    return api.patch(`/api/v1/plans/${id}`, data)
      .catch((err) => { throw new Error(err.message || 'Failed to update plan.'); });
  },

  /** DELETE /api/v1/plans/:id  (soft delete) */
  async deletePlan(id) {
    return api.delete(`/api/v1/plans/${id}`)
      .catch((err) => { throw new Error(err.message || 'Failed to delete plan.'); });
  },

  /** PATCH /api/v1/plans/:id/restore */
  async restorePlan(id) {
    return api.patch(`/api/v1/plans/${id}/restore`)
      .catch((err) => { throw new Error(err.message || 'Failed to restore plan.'); });
  },

  // ── Testimonials ───────────────────────────────────────────────────────────

  /** GET /api/v1/testimonials */
  async getTestimonials({ page = 1, limit = 50, search } = {}) {
    const params = new URLSearchParams();
    params.set('page',  String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    return api.get(`/api/v1/testimonials?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch testimonials.'); });
  },

  /**
   * POST /api/v1/testimonials
   * Payload: { full_name, email, job_title, company, rating, content }
   */
  async createTestimonial(data) {
    return api.post('/api/v1/testimonials', data)
      .catch((err) => { throw new Error(err.message || 'Failed to create testimonial.'); });
  },

  /**
   * PUT /api/v1/testimonials/:id
   * Payload: { full_name, email, job_title, company, rating, content, status, is_featured, is_active, rejection_reason }
   */
  async updateTestimonial(id, data) {
    return api.put(`/api/v1/testimonials/${id}`, data)
      .catch((err) => { throw new Error(err.message || 'Failed to update testimonial.'); });
  },

  /** DELETE /api/v1/testimonials/:id */
  async deleteTestimonial(id) {
    return api.delete(`/api/v1/testimonials/${id}`)
      .catch((err) => { throw new Error(err.message || 'Failed to delete testimonial.'); });
  },

  /**
   * PUT /api/v1/testimonials/:id/status — approve or reject.
   * Distinct from updateTestimonial: this endpoint also records who approved it
   * and when, which is what the public page filters on.
   * @param {string} id
   * @param {{ status: 'approved'|'rejected'|'pending', rejection_reason?: string, is_featured?: boolean }} payload
   */
  async updateTestimonialStatus(id, payload) {
    return api.put(`/api/v1/testimonials/${id}/status`, payload)
      .catch((err) => { throw new Error(err.message || 'Failed to update testimonial status.'); });
  },

  // ── Enterprise Contacts ──────────────────────────────────────────────────────

  /**
   * GET /api/v1/enterprise-contacts
   * Params: page, limit, search, status, sort_by, sort_order
   */
  async getEnterpriseContacts({ page = 1, limit = 20, search, status, sort_by = 'created_at', sort_order = 'desc' } = {}) {
    const params = new URLSearchParams();
    params.set('page',       String(page));
    params.set('limit',      String(limit));
    params.set('sort_by',    sort_by);
    params.set('sort_order', sort_order);
    if (search?.trim())                    params.set('search', search.trim());
    if (status && status !== 'all')        params.set('status', status);
    return api.get(`/api/v1/enterprise-contacts?${params.toString()}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch enterprise contacts.'); });
  },

  /**
   * GET /api/v1/enterprise-contacts/:id
   * Returns a single enterprise contact request.
   */
  async getEnterpriseContact(id) {
    return api.get(`/api/v1/enterprise-contacts/${id}`)
      .catch((err) => { throw new Error(err.message || 'Failed to fetch contact details.'); });
  },

  /**
   * PATCH /api/v1/enterprise-contacts/:id/mark-contacted
   */
  async markEnterpriseContactContacted(id) {
    return api.patch(`/api/v1/enterprise-contacts/${id}/mark-contacted`)
      .catch((err) => { throw new Error(err.message || 'Failed to mark contact as contacted.'); });
  },

  /**
   * PATCH /api/v1/enterprise-contacts/:id/mark-not-contacted
   */
  async markEnterpriseContactNotContacted(id) {
    return api.patch(`/api/v1/enterprise-contacts/${id}/mark-not-contacted`)
      .catch((err) => { throw new Error(err.message || 'Failed to mark contact as not contacted.'); });
  },

  /**
   * POST /api/v1/enterprise-contacts/:id/create-account
   */
  async createEnterpriseContactAccount(id) {
    return api.post(`/api/v1/enterprise-contacts/${id}/create-account`, {})
      .catch((err) => { throw new Error(err.message || 'Failed to create account for contact.'); });
  },

  /** GET /api/v1/admin/cache/days */
  async getAnalysisCacheDays() {
    return api.get('/api/v1/admin/cache/days')
      .catch((err) => { throw new Error(err.message || 'Failed to fetch cache days setting.'); });
  },

  /** PUT /api/v1/admin/cache/days — body: { days } */
  async updateAnalysisCacheDays(days) {
    return api.put('/api/v1/admin/cache/days', { days })
      .catch((err) => { throw new Error(err.message || 'Failed to update cache days setting.'); });
  },
};
